import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { PageHeader, Section } from '../components/Page';
import { tokens } from '../theme';
import { DeveloperArt, ManagerArt } from '../components/TeamArt';

const ENGAGE = [
  {
    step: '01',
    title: 'Browse what exists',
    body: 'Check the catalog first — many workflows already have a live IT Lab app.',
  },
  {
    step: '02',
    title: 'Request what doesn’t',
    body: 'Describe the problem, pick a preferred scoping date, and we review it with your department.',
  },
  {
    step: '03',
    title: 'Build and keep it running',
    body: 'We ship on Cloudflare inside Zero Trust, then track health on the status page.',
  },
];

const TEAM = [
  {
    name: 'Paramasivam Vannu Gopal',
    role: 'IT Senior Manager · IT',
    bio: 'Leads IT at AST and IT Lab.',
    focus: [] as string[],
    Art: ManagerArt,
    artBg: tokens.tint,
    artLabel: 'Vector illustration of Paramasivam Vannu Gopal, standing in front of a planning board',
  },
  {
    name: 'Mike Min Myat Oo',
    role: 'Developer · IT',
    bio: 'Designs, builds and runs IT Lab’s applications, from Traceboard and AST IT Hub to the NetSuite apps used by Finance, Procurement and Production Control.',
    focus: ['Web apps on Cloudflare', 'NetSuite SuiteScript', 'MES integration'],
    Art: DeveloperArt,
    artBg: '#EEF4FA',
    artLabel: 'Vector illustration of Mike Min Myat Oo, working at a laptop in front of a code window',
  },
];

function TeamSection() {
  return (
    <Section py={{ xs: 6, md: 9 }} sx={{ borderTop: `1px solid ${tokens.divider}` }}>
      <Stack spacing={4} component="div" aria-labelledby="team-h">
        <Stack spacing={1} maxWidth={640}>
          <Typography fontSize={13} fontWeight={700} letterSpacing="0.08em" textTransform="uppercase" color="primary.main">
            The team
          </Typography>
          <Typography id="team-h" variant="h2" sx={{ fontSize: { xs: 28, md: 40 }, lineHeight: 1.15 }}>
            The people behind IT Lab
          </Typography>
          <Typography fontSize={17} color="text.secondary">
            A small team that scopes each app with the department that needs it, builds it, and keeps it running.
          </Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {TEAM.map((m) => (
            <Box
              component="article"
              key={m.name}
              sx={{ border: `1px solid ${tokens.border}`, borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ bgcolor: m.artBg, borderBottom: '1px solid #D6E6F7' }}>
                <m.Art label={m.artLabel} />
              </Box>
              <Stack spacing={1.5} sx={{ p: 3, flex: 1 }}>
                <Box>
                  <Typography component="h3" fontSize={22} fontWeight={700} color={tokens.ink}>
                    {m.name}
                  </Typography>
                  <Typography fontSize={15} fontWeight={600} color="primary.main">
                    {m.role}
                  </Typography>
                </Box>
                <Typography color="#344054" sx={{ lineHeight: 1.6 }}>
                  {m.bio}
                </Typography>
                {m.focus.length > 0 && (
                  <Box
                    component="ul"
                    aria-label="Focus areas"
                    sx={{ listStyle: 'none', m: 0, mt: 'auto !important', p: 0, pt: 1.5, borderTop: `1px solid ${tokens.divider}`, display: 'flex', gap: 1, flexWrap: 'wrap' }}
                  >
                    {m.focus.map((f) => (
                      <Box component="li" key={f} sx={{ px: 1.25, py: 0.5, borderRadius: 999, bgcolor: tokens.strip, color: '#344054', fontSize: 13, fontWeight: 500 }}>
                        {f}
                      </Box>
                    ))}
                  </Box>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      </Stack>
    </Section>
  );
}

export function AboutPage() {
  return (
    <>
      <PageHeader
        title="About IT Lab"
        subtitle="The internal product studio of Advanced Substrate Technologies Pte. Ltd."
      />

      <Section>
        <Stack spacing={6}>
          <Typography
            component="p"
            sx={{ fontSize: { xs: 24, md: 32 }, lineHeight: 1.3, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.ink, maxWidth: 900 }}
          >
            We design and ship intranet applications — from brand-locked slide drafting to weekly IT delivery
            tracking and production MES visibility — so every department can move faster on a shared stack.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {ENGAGE.map((item) => (
              <Stack key={item.step} spacing={1.25} sx={{ pt: 2, borderTop: `3px solid ${tokens.blue}` }}>
                <Typography fontSize={13} fontWeight={700} letterSpacing="0.06em" color="primary.main">
                  {item.step}
                </Typography>
                <Typography variant="h3" fontSize={22}>
                  {item.title}
                </Typography>
                <Typography color="text.secondary">{item.body}</Typography>
              </Stack>
            ))}
          </Box>
        </Stack>
      </Section>

      <TeamSection />

      <Section band="subtle" py={{ xs: 6, md: 9 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={5} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Stack spacing={1.5} maxWidth={640}>
            <Typography variant="h2" fontSize={28}>
              How we work with departments
            </Typography>
            <Typography color="text.secondary">
              Start with the catalog to see what already exists. If you need a new workflow, submit a request
              with a preferred appointment date. We review it, schedule a short intake, and track delivery on
              this portal — alongside the status page for each live service.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} flex="none">
            <Button component={RouterLink} to="/apps" variant="contained" size="large">
              Browse applications
            </Button>
            <Button component={RouterLink} to="/request" variant="outlined" size="large">
              Request an app
            </Button>
          </Stack>
        </Stack>
      </Section>
    </>
  );
}
